/**
 * Pinia store for contractor portal authentication.
 * Contractors login separately from main app users and see their own data.
 * Supports admin impersonation to simulate contractor view.
 */

import { defineStore } from 'pinia'
import { contractorAuthApi } from '@/services/contractorService'
import type { Contractor } from '@/services/contractorService'
import type { ContractorLoginResponse } from '@/services/contractorService'
import { clearContractorToken, getContractorToken, hasUsableAuthToken, setContractorToken } from '@/utils/api'
import {
  getClientPortalFeatureFlags,
  mergeContractorMetadata,
  syncClientLogoUrl,
} from '@/utils/clientPortalFeatures'
import { readMustChangePassword } from '@/utils/clientPortalAuth'

const CONTRACTOR_KEY = 'contractor'
const REGION_KEY = 'contractor_region'
const IMPERSONATING_KEY = 'contractor_impersonating'
const CLIENT_PORTAL_LOGO_KEY = 'client_portal_logo_url'
const MUST_CHANGE_PASSWORD_KEY = 'contractor_must_change_password'
const PENDING_LOGIN_PASSWORD_KEY = 'contractor_pending_login_password'

/** localStorage may contain the literal "undefined" / invalid JSON from older writes — never throw from parse. */
function parseStoredContractor(raw: string | null): Contractor | null {
  if (raw == null || raw === '' || raw === 'undefined') return null
  try {
    const v = JSON.parse(raw) as unknown
    return v && typeof v === 'object' ? (v as Contractor) : null
  } catch {
    return null
  }
}

function persistContractor(contractor: Contractor | null | undefined): void {
  if (typeof window === 'undefined') return
  if (contractor != null) {
    localStorage.setItem(CONTRACTOR_KEY, JSON.stringify(contractor))
  } else {
    localStorage.removeItem(CONTRACTOR_KEY)
  }
}

export const useContractorAuthStore = defineStore('contractorAuth', {
  state: () => {
    const contractor = parseStoredContractor(localStorage.getItem(CONTRACTOR_KEY))
    return {
      token: getContractorToken(),
      contractor,
      region: localStorage.getItem(REGION_KEY) || 'johannesburg',
      isImpersonating: localStorage.getItem(IMPERSONATING_KEY) === 'true',
      mustChangePassword: localStorage.getItem(MUST_CHANGE_PASSWORD_KEY) === 'true',
      clientLogoUrl: syncClientLogoUrl(contractor),
    }
  },

  getters: {
    isAuthenticated: (state) => hasUsableAuthToken(state.token),
    currentContractor: (state) => state.contractor,
    portalFeatureFlags: (state) => getClientPortalFeatureFlags(state.contractor),
    vehicleFeaturesEnabled: (state) => getClientPortalFeatureFlags(state.contractor).vehicleFeaturesEnabled,
    bikeFeaturesEnabled: (state) => getClientPortalFeatureFlags(state.contractor).bikeFeaturesEnabled,
  },

  actions: {
    refreshClientLogoUrl() {
      this.clientLogoUrl = syncClientLogoUrl(this.contractor)
    },

    async login(emailOrPhone: string, password: string, useEmail = true) {
      const { resolvePortalLoginHost } = await import('@/utils/portalHost')
      const { usePortalConfigStore } = await import('@/stores/portalConfigStore')
      const portalConfig = usePortalConfigStore()
      if (portalConfig.isUnknownHost) {
        throw new Error('This domain is not registered to a client. Login is disabled.')
      }
      const portalHost = resolvePortalLoginHost()
      const data = await contractorAuthApi.login(emailOrPhone, password, useEmail, portalHost)
      this.setContractorSession(data)
      localStorage.removeItem(IMPERSONATING_KEY)
      return data
    },

    setMustChangePassword(required: boolean) {
      this.mustChangePassword = required
      if (typeof window === 'undefined') return
      if (required) {
        localStorage.setItem(MUST_CHANGE_PASSWORD_KEY, 'true')
      } else {
        localStorage.removeItem(MUST_CHANGE_PASSWORD_KEY)
      }
    },

    setPendingLoginPassword(password: string) {
      if (typeof window === 'undefined') return
      sessionStorage.setItem(PENDING_LOGIN_PASSWORD_KEY, password)
    },

    getPendingLoginPassword(): string {
      if (typeof window === 'undefined') return ''
      return sessionStorage.getItem(PENDING_LOGIN_PASSWORD_KEY) || ''
    },

    clearMustChangePassword() {
      this.setMustChangePassword(false)
      if (typeof window !== 'undefined') {
        sessionStorage.removeItem(PENDING_LOGIN_PASSWORD_KEY)
      }
    },

    /** Set session from impersonate or login response */
    setContractorSession(data: ContractorLoginResponse) {
      this.token = data.token
      this.contractor = data.contractor
      this.region = data.region || 'johannesburg'
      setContractorToken(data.token)
      persistContractor(data.contractor)
      localStorage.setItem(REGION_KEY, this.region)
      localStorage.setItem('region', this.region)
      this.setMustChangePassword(readMustChangePassword(data))
      this.refreshClientLogoUrl()
      void import('@/stores/portalConfigStore').then(({ usePortalConfigStore }) => {
        usePortalConfigStore().mergeFromContractor(this.contractor)
      })
    },

    /** Start impersonating a contractor (admin only). Call contractorAdminApi.impersonate(id) first. */
    startImpersonating(data: ContractorLoginResponse, adminClient?: Contractor | null) {
      if (adminClient) {
        data = {
          ...data,
          contractor: mergeContractorMetadata(data.contractor, adminClient.metadata) as Contractor,
        }
      }
      this.setContractorSession(data)
      this.isImpersonating = true
      localStorage.setItem(IMPERSONATING_KEY, 'true')
    },

    logout() {
      this.token = null
      this.contractor = null
      this.isImpersonating = false
      this.mustChangePassword = false
      this.clientLogoUrl = ''
      clearContractorToken()
      localStorage.removeItem(CONTRACTOR_KEY)
      localStorage.removeItem(REGION_KEY)
      localStorage.removeItem(IMPERSONATING_KEY)
      localStorage.removeItem(CLIENT_PORTAL_LOGO_KEY)
      localStorage.removeItem(MUST_CHANGE_PASSWORD_KEY)
      if (typeof window !== 'undefined') {
        sessionStorage.removeItem(PENDING_LOGIN_PASSWORD_KEY)
      }
    },

    /** Exit impersonation and return to admin contractor management */
    exitImpersonation() {
      this.logout()
    },

    async fetchProfile() {
      if (!this.token) {
        throw new Error('Not authenticated')
      }
      const previous = this.contractor
      const data = await contractorAuthApi.getProfile(this.token, this.region)
      let next = data.contractor
      if (previous && next) {
        next = mergeContractorMetadata(next, previous.metadata) as Contractor
      }
      this.contractor = next
      persistContractor(next)
      if (readMustChangePassword(data)) {
        this.setMustChangePassword(true)
      }
      this.refreshClientLogoUrl()
      const { usePortalConfigStore } = await import('@/stores/portalConfigStore')
      usePortalConfigStore().mergeFromContractor(next)
      return { ...data, contractor: next }
    },

    /** Restore from localStorage (e.g. on page load) */
    restoreFromStorage() {
      this.token = getContractorToken()
      if (!hasUsableAuthToken(this.token)) {
        this.logout()
        return
      }
      this.contractor = parseStoredContractor(localStorage.getItem(CONTRACTOR_KEY))
      this.region = localStorage.getItem(REGION_KEY) || 'johannesburg'
      this.isImpersonating = localStorage.getItem(IMPERSONATING_KEY) === 'true'
      this.mustChangePassword = localStorage.getItem(MUST_CHANGE_PASSWORD_KEY) === 'true'
      this.refreshClientLogoUrl()
    },
  },
})
